import React, { useEffect, useRef, useState } from "react";
import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getMetricHeaders } from "../../ApiHeaders/apiHeadersTimeseries";

interface MatchedItem {
  name: string;
  path?: string | null;
}

interface PIElementDropdownProps {
  selectedPIElement: string;
  onPIElementChange: (value: string, obj?: MatchedItem) => void;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
}

const PIElementDropdown: React.FC<PIElementDropdownProps> = ({
  selectedPIElement,
  onPIElementChange,
  disabled = false,
  className = "",
  onFocus,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<MatchedItem[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);



  const fetchOptions = async (search: string) => {
    if (!search) {
      setOptions([]);
      setOpen(false);
      return;
    }

    try {
      const headers = await getMetricHeaders();
      const body = {
        name: search,
        type: "pi",
      };

      const res = await apiClientTimeseries.post(
        "/reference-data/metric-metadata",
        body,
        { headers }
      );

      const items: MatchedItem[] =
        res?.data?.matched_items?.sort((a: MatchedItem, b: MatchedItem) =>
          a.name.localeCompare(b.name)
        ) || [];

      setOptions(items);
      setOpen(true);
      setHighlight(0);
    } catch (err) {
      console.error("PI Element fetch error:", err);
      setOptions([]);
    }
  };



  const handleTyping = (value: string) => {
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchOptions(value);
    }, 700);
  };



  const handleSelect = (item: MatchedItem) => {
    onPIElementChange(item.name, item);
    setQuery(item.name);
    setOpen(false);
  };



  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0) handleSelect(options[highlight]);
    }

    if (e.key === "Escape") {
      setOpen(false);
    }
  };

 
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  

  useEffect(() => {
    setQuery(selectedPIElement || "");
  }, [selectedPIElement]);

  

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        type="text"
        disabled={disabled}
        value={query}
        placeholder="Search PI Element"
        onFocus={() => {
          onFocus?.();
          if (options.length > 0) setOpen(true);
        }}
        onChange={(e) => handleTyping(e.target.value)}
        onKeyDown={handleKeyDown}
        className={`w-full p-2 border rounded-lg text-sm bg-white ${
          disabled ? "bg-[#f9f9f9] cursor-not-allowed" : ""
        }`}
      />

      {open && options.length > 0 && (
        <ul className="absolute z-50 bg-white border mt-1 w-full rounded-lg shadow-md max-h-60 overflow-y-auto text-sm">
          {options.map((opt, idx) => (
            <li
              key={opt.name}
              onClick={() => handleSelect(opt)}
              onMouseEnter={() => setHighlight(idx)}
              className={`px-3 py-2 cursor-pointer truncate ${
                idx === highlight
                  ? "bg-blue-100 text-black"
                  : "hover:bg-gray-100"
              }`}
            >
              {opt.name}
            </li>
          ))}
        </ul>
      )}

      {open && options.length === 0 && query.length > 0 && (
        <div className="absolute z-50 bg-white border mt-1 w-full rounded-lg shadow-md text-sm px-3 py-2 text-gray-500">
          No results found
        </div>
      )}
    </div>
  );
};

export default PIElementDropdown;
